import { useTranslation } from "react-i18next";
import {
  LOG_ROLES_BO,
  LOG_ROLES_CLIENT,
  LOG_TEMPLATES_BY_ENTITY,
  LogRole,
  NotificationTemplate,
} from "@/constants/log-templates.constants";
import { HelpTooltip } from "@/components/ui/customs/help-tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LogPermissionDto, LogPermissionEntryDto } from "@/types/api/api-types";
import { useRoleLabels } from "@/hooks/useEnumLabels";
import { useLogTemplateTexts } from "@/hooks/useLogTemplateTexts";

const NotificationDetails = ({ notification }: { notification: NotificationTemplate }) => {
  const { t } = useTranslation("boParameters");
  const locations = [
    notification.visibility.inGlobalNotifications && t("logPermissionsTable.locationGlobalBell"),
    notification.visibility.inExpeditionNotifications &&
      t("logPermissionsTable.locationExpeditionNotifications"),
    notification.visibility.inExpeditionNotes && t("logPermissionsTable.locationExpeditionNotes"),
  ].filter(Boolean);

  const isTargeted = !!notification.expectsRecipients;
  const scope = isTargeted
    ? t("logPermissionsTable.scopeTargeted")
    : t("logPermissionsTable.scopeGlobal");
  const scopeColor = isTargeted ? "text-amber-700" : "text-sky-700";

  const hasChannel = !!notification.slack?.channel;
  const slack = notification.slack
    ? hasChannel
      ? t("logPermissionsTable.slackChannel", { channel: notification.slack!.channel })
      : t("logPermissionsTable.slackDirect")
    : null;
  const slackColor = hasChannel ? "text-emerald-700" : "text-fuchsia-700";

  return (
    <div className="space-y-2 rounded-md bg-blue-50/70 px-3 py-2 text-xs">
      {locations.length > 0 && (
        <div>
          <div className="mb-0.5 text-[10px] font-semibold tracking-wide text-slate-500 uppercase">
            {t("logPermissionsTable.locationsTitle")}
          </div>
          <div className="text-slate-800">{locations.join(" · ")}</div>
        </div>
      )}
      {notification.visibility.isUrgent && (
        <div className="font-semibold text-red-600">{t("logPermissionsTable.urgent")}</div>
      )}
      <div>
        <div className="mb-0.5 text-[10px] font-semibold tracking-wide text-slate-500 uppercase">
          {t("logPermissionsTable.scopeTitle")}
        </div>
        <div className={scopeColor}>{scope}</div>
      </div>
      {slack && (
        <div>
          <div className="mb-0.5 text-[10px] font-semibold tracking-wide text-slate-500 uppercase">
            {t("logPermissionsTable.slackTitle")}
          </div>
          <div className={slackColor}>{slack}</div>
        </div>
      )}
    </div>
  );
};

type LogPermissionsTableProps = {
  isBoMode: boolean;
  permissions: LogPermissionDto[];
  onToggle: (action: LogPermissionEntryDto["action"], role: LogRole, checked: boolean) => void;
};

export const LogPermissionsTable = ({
  isBoMode,
  permissions,
  onToggle,
}: LogPermissionsTableProps) => {
  const { t } = useTranslation("boParameters");
  const roleLabels = useRoleLabels();
  const { getTitle, getDescription, getEntityLabel } = useLogTemplateTexts();
  const isChecked: (action: string, role: LogRole) => boolean = (action, role) =>
    permissions.some(
      (p) => p.action === action && p.role === role && p.isBo === isBoMode && p.canRead,
    );

  const roles = isBoMode ? LOG_ROLES_BO : LOG_ROLES_CLIENT;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <div className="min-w-[600px]">
        <div className="flex bg-gray-50 px-6 py-3">
          <span className="w-[30%] flex-none text-sm font-medium text-gray-500">
            {t("logPermissionsTable.permissionHeader")}
          </span>
          {roles.map((role) => (
            <span
              key={role}
              className="flex-1 text-center text-[.75rem] font-medium text-gray-500 lg:text-sm"
            >
              {roleLabels[role]}
            </span>
          ))}
        </div>

        <Accordion
          type="multiple"
          defaultValue={Object.keys(LOG_TEMPLATES_BY_ENTITY)}
          className="divide-y divide-gray-100"
        >
          {Object.entries(LOG_TEMPLATES_BY_ENTITY).map(([entityType, actions]) => (
            <AccordionItem key={entityType} value={entityType} className="border-b-0">
              <AccordionTrigger className="mt-6 px-6 py-2 text-lg font-semibold tracking-wide uppercase hover:bg-gray-100/70 hover:no-underline data-[state=closed]:bg-gray-50/30 data-[state=closed]:text-gray-400 data-[state=open]:bg-gray-50/70 data-[state=open]:text-blue-600">
                <div className={!isBoMode ? "text-green-400" : ""}>
                  {getEntityLabel(entityType)}{" "}
                  <span className="ml-1 text-xs font-light">({actions.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0">
                <div className="divide-y divide-gray-100">
                  {actions.map((action) => (
                    <div
                      key={action.key}
                      className="flex items-center px-6 transition-colors odd:bg-white even:bg-gray-50/50 hover:bg-gray-100/60"
                    >
                      <div className="w-[30%] flex-none py-3 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <span>{getTitle(action.key)}</span>
                          <HelpTooltip
                            content={getDescription(action.key) ?? ""}
                            iconClassName="text-gray-400"
                          />
                        </div>
                        {action.notification && (
                          <Accordion type="single" collapsible className="mt-1">
                            <AccordionItem value="notif" className="border-none">
                              <AccordionTrigger className="bg-orange-500/10 px-2 py-1 text-sm font-bold text-orange-700 hover:no-underline">
                                {t("logPermissionsTable.notification")}
                              </AccordionTrigger>
                              <AccordionContent>
                                <NotificationDetails notification={action.notification} />
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        )}
                      </div>
                      {roles.map((role) => (
                        <div key={role} className="flex flex-1 justify-center">
                          <label className="flex cursor-pointer items-center justify-center px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isChecked(action.key, role)}
                              onChange={(e) => onToggle(action.key, role, e.target.checked)}
                              className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-gray-800"
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};
